import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_material from '@amcharts/amcharts4/themes/material';
import { IRoseChartProps } from '../../typings/chart';
import guid from '../../generic/Guid';
import { debounce } from 'underscore';
import { Loading } from '../Loading/Loading';
import styles from './Chart.module.scss';
import { LegendColors } from '../../generic/common';
import './amchart.css';

am4core.useTheme(am4themes_material);
// am4core.options.commercialLicense = true;
am4core.options.queue = true;

export default class RoseChart extends Component<
  IRoseChartProps,
  { loading: boolean }
> {
  amChart!: am4charts.RadarChart;
  GUID = guid();
  constructor(props: any) {
    super(props);
    this.state = { loading: false };
  }

  componentDidMount() {
    this.createChart();
    this.createSeries();
    this.createLegend();
  }

  componentWillUnmount() {
    if (this.amChart) {
      this.amChart.dispose();
    }
  }

  componentDidUpdate(oldProps: IRoseChartProps) {
    if (oldProps.data !== this.props.data) {
      this.updateChartData();
    }
    if (oldProps.series !== this.props.series) {
      this.createSeries();
    }
    if (oldProps.angle !== this.props.angle) {
      this.amChart.startAngle = this.props.angle;
      this.amChart.endAngle = 360 + this.props.angle;
    }
  }

  createChart = () => {
    const chart: am4charts.RadarChart = am4core.create(
      this.GUID,
      am4charts.RadarChart
    );
    chart.responsive.enabled = true;
    chart.zoomOutButton.scale = 0.5;
    chart.marginTop = -12;
    chart.events.on('ready', () => {
      this.updateChartData();
      this.setState({ loading: false });
    });

    chart.startAngle = this.props.angle;
    chart.endAngle = 360 + this.props.angle;
    chart.colors.list = LegendColors.reverse().map((color) => am4core.color(color));
    chart.colors.step = 2;
    this.amChart = chart;

    const categoryAxis = chart.xAxes.push(
      new am4charts.CategoryAxis<am4charts.AxisRendererCircular>()
    );
    categoryAxis.dataFields.category = 'category';
    categoryAxis.renderer.labels.template.location = 0.5;
    categoryAxis.renderer.tooltipLocation = 0.5;
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
    const valueAxis = chart.yAxes.push(
      new am4charts.ValueAxis<am4charts.AxisRendererRadial>()
    );
    // valueAxis.tooltip.disabled = true;
    valueAxis.renderer.labels.template.horizontalCenter = 'left';
    valueAxis.renderer.labels.template.adapter.add('text', (label) => {
      return label + this.props.unitX;
    });

    valueAxis.min = 0;
  };

  exportChartAsImage = () => {
    this.amChart.exporting.getImage("jpg").then((imgData) => {
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${this.props.downloadName}.jpg` ?? 'Downloaded Chart'
      link.click();
    });
  };

  createSeries = () => {
    if (this.amChart !== undefined) {
      this.amChart.series.clear();
      this.props.series.forEach((s) => {
        const series = this.amChart!.series.push(
          new am4charts.RadarColumnSeries()
        );
        series.dataFields.categoryX = 'category';
        series.dataFields.valueY = s;
        series.name = s;
        series.strokeWidth = 0;
        series.columns.template.tooltipText =
          '{name}: {valueY.value}' + this.props.unitX;
        series.fillOpacity = 1;
        series.stacked = true;
      });
    }
  };

  createLegend = () => {
    this.amChart.legend = new am4charts.Legend();
  };

  updateChartData = debounce(() => {
    if (this.amChart.data !== this.props.data) {
      this.setState({ loading: true });
      this.amChart.data = this.props.data;
      this.setState({ loading: false });
    }
  }, 50);

  render() {
    return (
      <div className={styles.root} id={this.GUID}>
        <Loading loading={this.state.loading}></Loading>
      </div>
    );
  }
}
